import videojs from 'video.js';
import './background.css';

const _compositionMap = new WeakMap();
const _isMultiStream = new WeakMap();
const _backgroundContainer = new WeakMap();

const DEFAULT_COLOR = "#000000";

/**
 * The Background module.
 * @memberof MediasitePlayer#
 */

/**
 * Configures the background for single stream presentations
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 */
class BackgroundPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        const Component = videojs.getComponent('Component');
        const backgroundComponent = new Component(this.player, { name: 'Background' });        
        backgroundComponent.addClass('mediasite-player__background');
        backgroundComponent.addClass('vjs-hidden');

        _backgroundContainer.set(this, backgroundComponent);

        this.player.ready(() => {
            this.player.layout().addChild(backgroundComponent);
        });
    }

    /**
     * Set the composition map on the background plugin
     * @type {object}
     * @example
     * player.background().compositionMap = {}
     */
    get compositionMap() {
        return _compositionMap.get(this);
    }

    set compositionMap(map) {
        _compositionMap.set(this, map);
        this.shouldUpdateBackground(map);
    }

    /**
     * Set whether or not there are more than one stream with this presentation. If so, this plugin will let the multistream plugin handle the background logic instead
     */ 
    get isMultiStream() {
        return _isMultiStream.get(this);
    }

    set isMultiStream(hasMultipleStreams) {
        _isMultiStream.set(this, hasMultipleStreams);
    }

    shouldUpdateBackground(compositionMap) {
        //don't bother creating a background here if we have multiple stream, let the multistream plugin handle that
        if (this.isMultiStream) {
            return;
        }

        const composition = compositionMap?.VisualCompositions[0];
        //if the video we're looking already fills the canvas, don't bother adding a background since it wouldn't be seen
        const doesFill = this.doesVisualElementFillCanvas(composition.VisualItems.find(item => item.ZIndex === 1));        
        if (doesFill) {
            return;
        }

        //grab the background image and color to determine what we should do about them
        const backgroundComposition = composition.VisualItems.find(item => item.ZIndex === 0);

        let image, color;
        if (backgroundComposition) {
            image = backgroundComposition.SourceId;
        }

        if (composition.BaseColor) {
            color = composition.BaseColor;
        }    

        //update the background if we have an image, or a color that isn't the default
        if (image || (color && color != DEFAULT_COLOR)) {
            this.addBackground(image, color);
        }
    }

    addBackground(backgroundImageUrl, color) {
        const backgroundContainer = _backgroundContainer.get(this);
        backgroundContainer.addChild(this.player.tech(true));

        backgroundContainer.el().style.setProperty('--background-color', color);
        backgroundContainer.el().style.setProperty('--background-url', `url('${backgroundImageUrl}')`);

        backgroundContainer.removeClass("vjs-hidden");
    }

    doesVisualElementFillCanvas(visualElement) {
        if (!visualElement) {
            return false;
        }
        const targetRect = visualElement?.TargetRect;

        return (targetRect.Height === 1 && targetRect.Width === 1);
    }

    clear() {
        const backgroundContainer = _backgroundContainer.get(this);
        if (backgroundContainer.hasClass('vjs-hidden')) {
            return;
        }

        const tech = this.player.tech(true);
        this.player.layout().addChild(tech, {}, 0);

        backgroundContainer.addClass('vjs-hidden');
    }

    dispose() {
        this.clear();
        super.dispose();
    }
}

export default BackgroundPlugin;
